import { Link, useParams } from "@tanstack/react-router";
import { ChevronRight, Pencil } from "lucide-react";
import { useState } from "react";
import { ItemComments } from "@/components/items/ItemComments";
import { ItemEditForm } from "@/components/items/ItemEditForm";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useItem, useUpdateItem } from "@/hooks/use-items";
import { formatDate } from "@/lib/format";

export function ItemDetailPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string };
  const { data: item, isLoading, isError, error, refetch } = useItem(itemId);
  const updateItem = useUpdateItem(itemId);
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <span>Engineering</span>
            <ChevronRight size={14} />
            <Link to="/dashboard/items">Items</Link>
            <ChevronRight size={14} />
            <strong>Loading…</strong>
          </div>
        </div>
        <div className="page">
          <div className="card">
            <div className="card__body">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-64 w-full mt-4" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <span>Engineering</span>
            <ChevronRight size={14} />
            <Link to="/dashboard/items">Items</Link>
            <ChevronRight size={14} />
            <strong>Error</strong>
          </div>
        </div>
        <div className="page">
          <ErrorState message={error?.message ?? "Failed to load item"} onRetry={() => refetch()} />
        </div>
      </>
    );
  }

  if (!item) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <span>Engineering</span>
            <ChevronRight size={14} />
            <Link to="/dashboard/items">Items</Link>
            <ChevronRight size={14} />
            <strong>Not found</strong>
          </div>
        </div>
        <div className="page">
          <div className="card">
            <div className="card__body" style={{ textAlign: "center", padding: "48px 22px" }}>
              <p className="t-muted" style={{ marginBottom: 12 }}>
                Item not found.
              </p>
              <Link to="/dashboard/items" className="btn-link">
                Back to items
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="topbar__crumbs">
          <span>Engineering</span>
          <ChevronRight size={14} />
          <Link to="/dashboard/items">Items</Link>
          <ChevronRight size={14} />
          <strong>{item.name}</strong>
        </div>
      </div>
      <div className="page">
        <div className="page-header">
          <div>
            <h1
              className="page-header__title"
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              {item.name}
              <StatusBadge status={item.status} />
            </h1>
          </div>
          {!editing && (
            <button type="button" className="btn btn--secondary" onClick={() => setEditing(true)}>
              <Pencil size={14} className="btn__icon" /> Edit
            </button>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__header">
            <h2 className="h-section">Details</h2>
          </div>
          <div className="card__body">
            {editing ? (
              <ItemEditForm
                item={item}
                onSave={(values) =>
                  updateItem.mutate(
                    {
                      name: values.name,
                      description: values.description ?? null,
                      status: values.status,
                    },
                    { onSuccess: () => setEditing(false) },
                  )
                }
                onCancel={() => setEditing(false)}
                isSaving={updateItem.isPending}
              />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="t-label">Status</span>
                    <div className="mt-1">
                      <StatusBadge status={item.status} />
                    </div>
                  </div>
                  <div>
                    <span className="t-label">Created</span>
                    <p className="mt-1">{formatDate(item.createdAt)}</p>
                  </div>
                </div>
                {item.description && (
                  <div className="text-sm">
                    <span className="t-label">Description</span>
                    <p className="mt-1">{item.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <h2 className="h-section">Comments</h2>
          </div>
          <div className="card__body">
            <ItemComments itemId={item.id} />
          </div>
        </div>
      </div>
    </>
  );
}
